/**
 * PricingSection Component
 * Landing page pricing plans — uses canonical SUBSCRIPTION_PLANS as single source of truth
 */

import { Link } from "react-router-dom";
import { Check, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SUBSCRIPTION_PLANS, formatPrice, formatCredits } from "@/types/subscription";

export const PricingSection = () => {
  return (
    <section id="pricing" className="py-20 px-6">
      <div className="container mx-auto max-w-7xl">
        <h2 className="text-4xl font-bold text-center text-primary mb-4">
          Simple, Transparent Pricing
        </h2>
        <p className="text-center text-foreground/70 mb-12 max-w-xl mx-auto">
          Start free, scale as you grow. No hidden fees.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 items-end">
          {SUBSCRIPTION_PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`p-6 rounded-2xl relative flex flex-col border transition-all duration-300 ${
                plan.popular
                  ? "border-primary bg-primary/5 shadow-xl shadow-primary/20 ring-1 ring-primary/40 -mt-6"
                  : "border-border bg-card hover:border-primary/50"
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                    <Sparkles className="w-3 h-3" />
                    Most Popular
                  </span>
                </div>
              )}
              <h3 className="text-xl font-bold text-foreground mb-1">{plan.name}</h3>
              <p className="text-sm text-muted-foreground mb-4">{plan.description}</p>
              <div className="mb-4">
                {plan.id === "enterprise" ? (
                  <span className="text-4xl font-bold text-foreground">Custom</span>
                ) : (
                  <>
                    <span className="text-4xl font-bold text-foreground">
                      {formatPrice(plan.price)}
                    </span>
                    {plan.price > 0 && (
                      <span className="text-muted-foreground ml-1">/month</span>
                    )}
                  </>
                )}
                <p className="text-sm text-muted-foreground mt-1">
                  {formatCredits(plan.creditsPerMonth)} credits/month
                </p>
              </div>
              <ul className="space-y-2 mb-6 flex-1">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-sm">
                    {feature.included ? (
                      <Check className="w-4 h-4 text-primary shrink-0" />
                    ) : (
                      <X className="w-4 h-4 text-muted-foreground/50 shrink-0" />
                    )}
                    <span
                      className={
                        feature.included
                          ? "text-foreground/80"
                          : "text-muted-foreground/50 line-through"
                      }
                    >
                      {feature.text}
                    </span>
                  </li>
                ))}
              </ul>
              <Button
                variant={plan.popular ? "hero" : "outline"}
                className="w-full mt-auto"
                asChild
              >
                <Link
                  to={
                    plan.id === "enterprise"
                      ? "/contact"
                      : plan.id === "free"
                      ? "/sign-up"
                      : "/pricing"
                  }
                >
                  {plan.cta}
                </Link>
              </Button>
            </div>
          ))}
        </div>
        <p className="text-center text-muted-foreground mt-8 text-sm">
          All paid plans include a 14-day free trial. No credit card required.
        </p>
      </div>
    </section>
  );
};
